import { useSelector } from 'react-redux';
import { StandardModal, ModalBody, ModalSize } from '../../Components/Fundamentals/Modal/StandardModal';
import { VossTierSelector } from '../../Components/OrderPlacement/ProductComponents/Voss/VossTierSelector';
import { VossTierOptionsCard } from '../../Components/OrderPlacement/ProductComponents/Voss/VossTierOptionsCard';
import { VossLicenseOptionsCard } from '../../Components/OrderPlacement/ProductComponents/Voss/VossLicenseOptionsCard';
import { VossDiscountOptionsCard } from '../../Components/OrderPlacement/ProductComponents/Voss/VossDiscountOptionsCard';
import { VossPriceSummaryCard } from '../../Components/OrderPlacement/ProductComponents/Voss/VossPriceSummaryCard';
import AddVossDiscountModal from '../../Components/Modals/AddVossDiscount/AddVossDiscountModal';
import useModal from '../../Hooks/useModal';
import useVossOrder from '../../Hooks/useVossOrder';
import useUserAccess from '../../Hooks/useUserAccess';
import { useIntermediariesWithSelected } from '../../Hooks/useIntermediariesWithSelected';
import Modals from '../../Constants/modals';
import { UserRoles } from '../../Constants/userRoles';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

function VossProductContainer({ order, product, editOrderline }) {
    const { openModal, closeModal, isModalOpen } = useModal();
    const { t } = useTranslation();
    const { hasReadWriteAccess } = useUserAccess();
    const priceLoading = useSelector(state => state.CommonReducer.dataLoading?.vossDryRun);
    const {
        offer,
        subscription,
        selections,
        migrationSourceSubscription,
        intermediaries,
        dryRunResult,
        vossPriceCalculation,
        scheduledChange,
        selectablePlans,
        tierChangeLoading,
        updateSelection,
        toggleOptionalGroup,
        selectExclusiveGroup,
        updateLicense,
        selectTier,
        ready,
    } = useVossOrder();

    const intermediariesWithSelected = useIntermediariesWithSelected(intermediaries, subscription);

    useEffect(() => {
        if (ready && !selections.tierId) {
            openModal(Modals.VossTierSelect);
        }
    }, [ready]);

    if (!ready) return null;

    const handleTierSelect = (tierId) => {
        selectTier(tierId);
        closeModal();
    };

    const handleAddDiscount = (discount) => {
        const current = selections.discountAgreements ?? [];
        if (!current.some(d => d.discountAgreementId === discount.discountAgreementId)) {
            updateSelection('discountAgreements', [...current, discount]);
        }
        closeModal();
    };

    return (
        <>
            <div className="row">
                <div className="col-md-5 pl-0">
                    <VossTierOptionsCard
                        offer={offer}
                        selections={selections}
                        selectablePlans={selectablePlans}
                        migrationSourceSubscription={migrationSourceSubscription}
                        loading={tierChangeLoading}
                        intermediaries={hasReadWriteAccess(UserRoles.PartnerSales) ? intermediariesWithSelected : null}
                        subscription={subscription}
                        scheduledChange={scheduledChange}
                        onOpenTierModal={() => openModal(Modals.VossTierSelect)}
                        onUpdateSelection={updateSelection}
                        onToggleOptionalGroup={toggleOptionalGroup}
                        onSelectExclusiveGroup={selectExclusiveGroup}
                    />
                </div>


                <div className="col-md-7 pr-0">
                    <div className="d-flex flex-column gap-24">
                        {selections.tierId && (<>
                            <VossDiscountOptionsCard
                                offer={offer}
                                dryRunResult={dryRunResult}
                                selections={selections}
                                onOpenDiscountModal={() => openModal(Modals.AddVossDiscountModal)}
                                onUpdateSelection={updateSelection}
                            />
                            <VossLicenseOptionsCard
                                offer={offer}
                                selections={selections}
                                onUpdateLicense={updateLicense}
                            />
                        </>)}
                    </div>
                </div>
            </div>

            {selections.tierId && (
                <>
                    <VossPriceSummaryCard
                        offer={offer}
                        priceCalculation={vossPriceCalculation}
                        priceLoading={priceLoading}
                        order={order}
                        product={product}
                        editOrderline={editOrderline}
                    />
                </>
            )}

            {isModalOpen(Modals.VossTierSelect) && (
                <StandardModal title={t('vossTierSelectModal.title')} size={ModalSize.X_LARGE} onClose={closeModal}>
                    <ModalBody>
                        <VossTierSelector tiers={offer.tiers} onSelect={handleTierSelect} />
                    </ModalBody>
                </StandardModal>
            )}
            {isModalOpen(Modals.AddVossDiscountModal) && (
                <AddVossDiscountModal
                    discounts={offer.discounts ?? []}
                    onAddDiscount={handleAddDiscount}
                />
            )}
        </>
    );
}

export default VossProductContainer;